/**
 * Shared form body used by both CreateSpaceModal and ManageSpaceModal.
 *
 * Renders the name (+ emoji picker), about, and sources fields.
 * The caller supplies:
 *   - `formId` — the HTML form id (so footer buttons can submit via `form=`)
 *   - `messages` — i18n strings for labels / validation (each modal has its own)
 *   - `sourceRowRenderer` — how to render a single source row (create uses SmartLink, manage uses brand icons)
 *   - `onSubmit` — called with validated form values
 *   - `initialValues` — pre-populate fields
 *   - `emojiProvider` / `cloudId` — for the emoji picker
 *   - `cloudId` — passed to RovoLinkPicker for source search
 */
import React, { useCallback, useRef, useState } from 'react';

import { useIntl, type MessageDescriptor } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import type { EmojiId, EmojiProvider } from '@atlaskit/emoji';
import { ResourcedEmoji } from '@atlaskit/emoji/element';
import { EmojiPicker } from '@atlaskit/emoji/picker';
import Form, { Field, RequiredAsterisk } from '@atlaskit/form';
import AddIcon from '@atlaskit/icon/core/add';
import DeleteIcon from '@atlaskit/icon/core/delete';
import EmojiAddIcon from '@atlaskit/icon/core/emoji-add';
import { LinkPicker, type LinkPickerProps } from '@atlaskit/link-picker';
import { fg } from '@atlaskit/platform-feature-flags';
import { Popup } from '@atlaskit/popup';
import { Box, Flex, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
const RovoLinkPicker = React.lazy(() =>
	import(
		/* webpackChunkName: "rovo-link-picker" */
		'@atlassian/rovo-link-picker/RovoLinkPicker'
	).then((mod) => ({ default: mod.default })),
);

import { SPACE_ABOUT_MAX_LENGTH, SPACE_NAME_MAX_LENGTH } from '../../common/constants';
import type { SpaceSource } from '../create-space-modal/types';
import { EmptySourcesState } from '../space-landing-page/EmptySourcesState';

import { detectSourceType } from './utils';

const styles = cssMap({
	rovoLinkPickerWrapper: {
		maxHeight: '450px',
		overflowY: 'auto',
	},
	sourceRow: {
		paddingBlock: token('space.075'),
	},
	sourceTitleRow: {
		minWidth: '0px',
		flex: 1,
	},
	emojiPickerTrigger: {
		paddingInlineStart: token('space.100'),
		backgroundColor: 'transparent',
	},
	sourcesContainer: {
		borderRadius: token('radius.medium'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		alignSelf: 'stretch',
	},
	emptySourcesContainer: {
		borderRadius: token('radius.medium'),
		borderStyle: 'dashed',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		paddingTop: token('space.400'),
		paddingRight: token('space.400'),
		paddingBottom: token('space.400'),
		paddingLeft: token('space.400'),
		alignSelf: 'stretch',
	},
});

export type SpaceModalFormMessages = {
	requiredFieldsHint: MessageDescriptor;
	nameLabel: string;
	nameRequired: string;
	emojiPickerLabel: string;
	aboutLabel: string;
	sourcesLabel: string;
	addSources: string;
	removeSource: (name: string | undefined) => string;
	noSourcesTitle: string;
	noSources: string;
};

export type SpaceModalFormValues = {
	name: string;
	about: string;
	sources: SpaceSource[];
};

type SpaceSourcesLayoutProps = {
	sources: SpaceSource[];
	onRemove: (ari: string) => void;
	renderSourceRow: (source: SpaceSource) => React.ReactNode;
	addSourcesTrigger: React.ReactNode;
	addSourcesTriggerPrimary: React.ReactNode;
	removeSourceLabel: (name: string | undefined) => string;
	noSourcesTitle: string;
	noSources: string;
};

const SpaceSourcesLayout = ({
	sources,
	onRemove,
	renderSourceRow,
	addSourcesTrigger,
	addSourcesTriggerPrimary,
	removeSourceLabel,
	noSourcesTitle,
	noSources,
}: SpaceSourcesLayoutProps): React.JSX.Element => (
	<Stack space="space.100">
		{sources.length === 0 ? (
			<Box xcss={styles.emptySourcesContainer}>
				<EmptySourcesState
					title={noSourcesTitle}
					description={noSources}
					headingSize="xsmall"
					action={addSourcesTriggerPrimary}
				/>
			</Box>
		) : (
			<>
				<Box xcss={styles.sourcesContainer}>
					<Stack space="space.0">
						{sources.map((source) => (
							<Flex
								key={source.ari}
								direction="row"
								justifyContent="space-between"
								alignItems="center"
								xcss={styles.sourceRow}
							>
								<Flex
									direction="row"
									alignItems="center"
									gap="space.100"
									xcss={styles.sourceTitleRow}
								>
									{renderSourceRow(source)}
								</Flex>
								<IconButton
									icon={DeleteIcon}
									appearance="subtle"
									spacing="compact"
									label={removeSourceLabel(source.name)}
									onClick={() => onRemove(source.ari)}
									testId={`space-modal-remove-source-${source.ari}`}
								/>
							</Flex>
						))}
					</Stack>
				</Box>
				<Flex>{addSourcesTrigger}</Flex>
			</>
		)}
	</Stack>
);

type SpaceSourcesFieldBodyProps = {
	fieldProps: {
		value: unknown;
		onChange: (next: SpaceSource[]) => void;
	};
	cloudId: string;
	plugins: Parameters<typeof LinkPicker>[0]['plugins'];
	addSourcesLabel: string;
	removeSourceLabel: (name: string | undefined) => string;
	renderSourceRow: (source: SpaceSource) => React.ReactNode;
	testId: string;
	noSourcesTitle: string;
	noSources: string;
};

const SpaceSourcesFieldBody = ({
	fieldProps,
	cloudId,
	plugins,
	addSourcesLabel,
	removeSourceLabel,
	renderSourceRow,
	testId,
	noSourcesTitle,
	noSources,
}: SpaceSourcesFieldBodyProps): React.JSX.Element => {
	const [isPickerOpen, setPickerOpen] = useState(false);
	const sources = (fieldProps.value as SpaceSource[] | undefined) ?? [];

	const fieldPropsRef = useRef(fieldProps);
	fieldPropsRef.current = fieldProps;

	const handleSubmit: LinkPickerProps['onSubmit'] = useCallback(({ url, displayText, title }) => {
		const { onChange, value } = fieldPropsRef.current;
		const current = (value as SpaceSource[] | undefined) ?? [];
		onChange([
			...current,
			{
				ari: url,
				url,
				name: displayText ?? title ?? url,
				sourceType: detectSourceType(url),
				conversationIds: [],
			} satisfies SpaceSource,
		]);
		setPickerOpen(false);
	}, []);

	const makeAddSourcesTrigger = (appearance: 'default' | 'primary') => (
		<Popup
			isOpen={isPickerOpen}
			onClose={() => setPickerOpen(false)}
			placement="bottom-start"
			zIndex={510}
			content={() =>
				fg('rovo_spaces_use_rovo_link_picker') ? (
					<React.Suspense fallback={null}>
						<Box xcss={styles.rovoLinkPickerWrapper}>
							<RovoLinkPicker
								cloudId={cloudId}
								onSubmit={handleSubmit}
								onCancel={() => setPickerOpen(false)}
							/>
						</Box>
					</React.Suspense>
				) : (
					<LinkPicker
						plugins={plugins}
						onSubmit={handleSubmit}
						onCancel={() => setPickerOpen(false)}
						adaptiveHeight
						recentSearchListSize={3}
					/>
				)
			}
			trigger={(triggerProps) => (
				<Button
					{...triggerProps}
					appearance={appearance}
					iconBefore={AddIcon}
					type="button"
					onClick={() => setPickerOpen((prev) => !prev)}
					testId={`${testId}-add-sources`}
				>
					{addSourcesLabel}
				</Button>
			)}
		/>
	);

	return (
		<SpaceSourcesLayout
			sources={sources}
			onRemove={(ari) => fieldProps.onChange(sources.filter((s) => s.ari !== ari))}
			renderSourceRow={renderSourceRow}
			addSourcesTrigger={makeAddSourcesTrigger('default')}
			addSourcesTriggerPrimary={makeAddSourcesTrigger('primary')}
			removeSourceLabel={removeSourceLabel}
			noSourcesTitle={noSourcesTitle}
			noSources={noSources}
		/>
	);
};

export type SpaceModalFormProps = {
	formId: string;
	testId: string;
	messages: SpaceModalFormMessages;
	initialName?: string;
	initialAbout?: string;
	initialSources?: SpaceSource[];
	selectedEmoji: EmojiId | undefined;
	onEmojiSelect: (emojiId: EmojiId) => void;
	isEmojiPickerOpen: boolean;
	onEmojiPickerToggle: () => void;
	onEmojiPickerClose: () => void;
	emojiProvider: Promise<EmojiProvider>;
	cloudId: string;
	plugins: Parameters<typeof LinkPicker>[0]['plugins'];
	onSubmit: (values: SpaceModalFormValues) => void;
	renderSourceRow: (source: SpaceSource) => React.ReactNode;
};

export const SpaceModalForm = ({
	formId,
	testId,
	messages: msgs,
	initialName = '',
	initialAbout = '',
	initialSources = [],
	selectedEmoji,
	onEmojiSelect,
	isEmojiPickerOpen,
	onEmojiPickerToggle,
	onEmojiPickerClose,
	emojiProvider,
	cloudId,
	plugins,
	onSubmit,
	renderSourceRow,
}: SpaceModalFormProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [nameLength, setNameLength] = useState(initialName.length);
	const [aboutLength, setAboutLength] = useState(initialAbout.length);
	return (
		<Stack space="space.200">
			<Flex direction="column" gap="space.050">
				<Form<SpaceModalFormValues> onSubmit={onSubmit}>
					{({ formProps }) => (
						<form {...formProps} id={formId} name={formId}>
							<Text as="p" aria-hidden="true" size="small" color="color.text.subtle">
								{formatMessage(msgs.requiredFieldsHint, {
									asterisk: () => <RequiredAsterisk />,
								})}
							</Text>
							<Field
								name="name"
								label={msgs.nameLabel}
								isRequired
								defaultValue={initialName}
								validate={(value) =>
									value && String(value).trim() ? undefined : msgs.nameRequired
								}
								helperMessage={`${nameLength} / ${SPACE_NAME_MAX_LENGTH}`}
								component={({ fieldProps }) => (
									<Popup
										zIndex={510}
										isOpen={isEmojiPickerOpen}
										onClose={onEmojiPickerClose}
										placement="bottom-start"
										content={() => (
											<EmojiPicker emojiProvider={emojiProvider} onSelection={onEmojiSelect} />
										)}
										trigger={(triggerProps) => (
											<TextField
												{...fieldProps}
												maxLength={SPACE_NAME_MAX_LENGTH}
												onChange={(e) => {
													setNameLength(e.currentTarget.value.length);
													fieldProps.onChange(e);
												}}
												elemBeforeInput={
													<Pressable
														{...triggerProps}
														xcss={styles.emojiPickerTrigger}
														onClick={onEmojiPickerToggle}
														testId={`${testId}-emoji-picker-trigger`}
													>
														{selectedEmoji ? (
															<ResourcedEmoji
																emojiId={selectedEmoji}
																emojiProvider={emojiProvider}
																showTooltip
																fitToHeight={20}
															/>
														) : (
															<EmojiAddIcon label={msgs.emojiPickerLabel} />
														)}
													</Pressable>
												}
											/>
										)}
									/>
								)}
							/>
							<Field<string, HTMLTextAreaElement>
								name="about"
								label={msgs.aboutLabel}
								defaultValue={initialAbout}
								helperMessage={`${aboutLength} / ${SPACE_ABOUT_MAX_LENGTH}`}
								component={({ fieldProps }) => (
									<TextArea
										{...fieldProps}
										maxLength={SPACE_ABOUT_MAX_LENGTH}
										onChange={(e) => {
											setAboutLength(e.currentTarget.value.length);
											fieldProps.onChange(e);
										}}
									/>
								)}
							/>
							<Field name="sources" label={msgs.sourcesLabel} defaultValue={initialSources}>
								{({ fieldProps }) => (
									<SpaceSourcesFieldBody
										fieldProps={fieldProps}
										cloudId={cloudId}
										plugins={plugins}
										addSourcesLabel={msgs.addSources}
										removeSourceLabel={msgs.removeSource}
										renderSourceRow={renderSourceRow}
										testId={testId}
										noSourcesTitle={msgs.noSourcesTitle}
										noSources={msgs.noSources}
									/>
								)}
							</Field>
						</form>
					)}
				</Form>
			</Flex>
		</Stack>
	);
};
